import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { Check, HelpCircle } from "lucide-react";

interface MobileOptionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  capacity: number;
  setCapacity: (value: number) => void;
  duration: number;
  setDuration: (value: number) => void;
  isRoundTrip: boolean;
  setIsRoundTrip: (value: boolean) => void;
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  onDone: () => void;
}

export const MobileOptionsDrawer = ({
  isOpen,
  onClose,
  capacity,
  setCapacity,
  duration,
  setDuration,
  isRoundTrip,
  setIsRoundTrip,
  date,
  setDate,
  onDone
}: MobileOptionsDrawerProps) => {
  const [showCalendar, setShowCalendar] = useState(false);

  const partySizeOptions = Array.from({ length: 35 }, (_, i) => i + 6); // 6 to 40
  const durationOptions = Array.from({ length: 15 }, (_, i) => i + 2); // 2 to 16 hours

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    setShowCalendar(false);
  };

  const getDateOptions = () => {
    const today = new Date();
    const dateOptions = [];
    
    // Generate 21 days (3 weeks) starting from tomorrow
    for (let i = 1; i < 22; i++) {
      const currentDate = addDays(today, i);
      let label = "";
      
      if (i === 1) {
        label = "Tomorrow";
      } else {
        label = format(currentDate, "EEE, MMM d");
      }
      
      dateOptions.push({ label, date: currentDate });
    }
    
    return dateOptions;
  };

  // Check if sections are completed
  const isPartySizeCompleted = capacity >= 6;
  const isTripTypeCompleted = true; // Always completed since it has a default
  const isDateCompleted = date !== undefined;
  const isDurationCompleted = duration >= 1;

  // Check if all required fields are completed
  const allFieldsCompleted = isPartySizeCompleted && isTripTypeCompleted && isDateCompleted && isDurationCompleted;

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="bg-white">
        <DrawerHeader>
          <DrawerTitle className="text-center text-xl font-bold text-black">Enter Details For Your Trip</DrawerTitle>
        </DrawerHeader>
        
        <div className="p-6 space-y-2">
          {/* Date and Duration Side by Side - MOVED TO TOP */}
          <div className="flex gap-6">
            {/* Select Date */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Label className="text-lg font-semibold text-black">Select Date</Label>
                {isDateCompleted ? (
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                ) : (
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <HelpCircle className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>
              
              {!showCalendar ? (
                <div>
                  <ScrollArea className="h-32 w-full border border-gray-300 rounded-md">
                    <div className="p-2 space-y-1">
                      {getDateOptions().map((option, index) => (
                        <button
                          key={index}
                          onClick={() => setDate(option.date)}
                          className={cn(
                            "w-full text-left p-2 rounded font-medium transition-colors text-sm",
                            date && format(date, "yyyy-MM-dd") === format(option.date, "yyyy-MM-dd")
                              ? "bg-green-50 text-green-600 border border-green-200"
                              : "text-gray-600 hover:bg-gray-50"
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                  <button
                    onClick={() => setShowCalendar(true)}
                    className="mt-4 text-gray-600 hover:text-gray-800 font-medium text-sm underline decoration-2 decoration-cyan-400 underline-offset-2 hover:decoration-cyan-500"
                  >
                    Choose different date
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={handleDateSelect}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const selectedDate = new Date(date);
                      selectedDate.setHours(0, 0, 0, 0);
                      return selectedDate <= today;
                    }}
                    className="rounded-md border border-gray-300"
                  />
                  <button
                    onClick={() => setShowCalendar(false)}
                    className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                  >
                    ← Back to quick select
                  </button>
                </div>
              )}
            </div>

            {/* Duration */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Label className="text-lg font-semibold text-black">Duration</Label>
                {isDurationCompleted ? (
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                ) : (
                  <span className="text-red-500 text-lg">*</span>
                )}
              </div>
              <ScrollArea className="h-32 w-full border border-gray-300 rounded-md">
                <div className="p-2 space-y-1">
                  {durationOptions.map((hours) => (
                    <button
                      key={hours}
                      onClick={() => setDuration(hours)}
                      className={cn(
                        "w-full text-left p-2 rounded font-medium transition-colors text-sm",
                        duration === hours
                          ? "bg-green-50 text-green-600 border border-green-200"
                          : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      {hours} Hour{hours !== 1 ? 's' : ''}
                    </button>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-gray-600 mt-3">How long do you need the party bus?</p>
            </div>
          </div>

          {/* Party Size */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Label className="text-lg font-semibold text-black">Party size</Label>
              {isPartySizeCompleted ? (
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="h-3 w-3 text-white" />
                </div>
              ) : (
                <span className="text-red-500 text-lg">*</span>
              )}
            </div>
            <div className="w-full overflow-x-auto">
              <div className="flex gap-3 pb-4 min-w-max">
                {partySizeOptions.map((size) => (
                  <button
                    key={size}
                    onClick={() => setCapacity(size)}
                    className={cn(
                      "w-12 h-12 rounded-full border-2 flex items-center justify-center font-medium transition-colors flex-shrink-0",
                      capacity === size 
                        ? "border-green-400 bg-green-50 text-green-600" 
                        : "border-gray-300 text-gray-600 hover:border-gray-400"
                    )}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Trip Type */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Label className="text-lg font-semibold text-black">One Way or Round Trip</Label>
              {isTripTypeCompleted ? (
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="h-3 w-3 text-white" />
                </div>
              ) : (
                <span className="text-red-500 text-lg">*</span>
              )}
            </div>
            <div className="flex gap-6">
              <button
                onClick={() => setIsRoundTrip(false)}
                className={cn(
                  "flex-1 p-1 rounded-lg border-2 flex flex-col items-center gap-1 transition-colors",
                  !isRoundTrip 
                    ? "border-green-400 bg-green-400 text-white" 
                    : "border-gray-300 text-gray-600 hover:border-gray-400"
                )}
              >
                <div className="text-2xl">→</div>
                <span className="font-medium">One Way</span>
              </button>
              <button
                onClick={() => setIsRoundTrip(true)}
                className={cn(
                  "flex-1 p-1 rounded-lg border-2 flex flex-col items-center gap-1 transition-colors",
                  isRoundTrip 
                    ? "border-green-400 bg-green-400 text-white" 
                    : "border-gray-300 text-gray-600 hover:border-gray-400"
                )}
              >
                <div className="text-2xl">⟲</div>
                <span className="font-medium">Round Trip</span>
              </button>
            </div>
          </div>
        </div>

        <DrawerFooter className="pt-4">
          <Button
            onClick={onDone}
            disabled={!allFieldsCompleted}
            className={cn(
              "w-full font-semibold py-8 text-lg rounded-lg transition-all",
              allFieldsCompleted 
                ? "bg-gradient-to-r from-green-400 to-blue-400 hover:from-green-500 hover:to-blue-500 text-white" 
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            )}
          >
            Get Party Bus Quote
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
